import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import './Auth.css';

const DEFAULT_CONFIRMATION =
  'If an account exists for that email address, a password reset link is on its way.';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmation, setConfirmation] = useState(DEFAULT_CONFIRMATION);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.forgotPassword(email);
      // The API deliberately returns the same message whether or not an account
      // exists, so there is nothing to branch on here.
      setConfirmation(response.data?.message || DEFAULT_CONFIRMATION);
      setSubmitted(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="auth-container">
        <div className="auth-form">
          <h1>Check Your Email</h1>
          <div className="success">{confirmation}</div>
          <p className="auth-help">
            The link expires in 60 minutes and can only be used once. If it does not
            arrive within a few minutes, check your spam folder.
          </p>
          <button
            type="button"
            className="auth-button-secondary"
            onClick={() => {
              setSubmitted(false);
              setEmail('');
            }}
          >
            Use a different email
          </button>
          <p>
            <Link to="/login">Back to login</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h1>Forgot Password</h1>
        <p className="auth-help">
          Enter the email address you used for your account and we will send you a
          link to set a new password.
        </p>

        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="forgot-email">Email:</label>
            <input
              id="forgot-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus
            />
          </div>

          <button type="submit" disabled={loading} className="auth-button">
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <p>
          Remembered it? <Link to="/login">Back to login</Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
